import './form-chat.css'

import { useState } from 'react'

import { Modal } from '../../../Components/Common/Modal'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { Icon } from './Icons'

const language = cap.language.state || 'IT'
const t = translations[language]
const timeLocale = language === 'EN' ? 'en-GB' : 'it-IT'

/**
 * Formatta l'orario del messaggio in formato HH:mm
 * @param {Date} date - Data del messaggio
 * @returns {string} Orario formattato
 */
const formatTime = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(timeLocale, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Sanitizza il testo HTML del messaggio in paragrafi
 * @param {string} msg - Testo grezzo del messaggio
 * @returns {string[]} Paragrafi puliti
 */
const sanitizeMsg = (msg) =>
  msg
    ?.replace(/<br\s*\/?>/g, '\n')
    ?.replace(/<(?!img\s)[a-zA-Z][^>]*>/g, '')
    ?.replace(/(<img\s+[^>]+>)/gi, '\n$1\n')
    ?.replace(/<\/p>/gi, '\n')
    ?.replace(/<\/a>/gi, '')
    ?.split('\n')
    ?.map((p) => p?.trim())
    ?.filter(Boolean)

const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g

/**
 * Verifica che un URL sia sicuro (protocollo http/https, no javascript:, data:, vbscript:)
 * @param {string} url - URL da verificare
 * @returns {boolean} true se l'URL è sicuro da usare come href
 */
const isSafeUrl = (url) => {
  try {
    const parsed = new URL(url)
    return ['http:', 'https:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

/**
 * Converte URL in link cliccabili all'interno di un frammento di testo
 * @param {string} text - Testo che può contenere URL
 * @param {string} keyPrefix - Prefisso per le chiavi React
 * @returns {import('react').ReactNode[]} Frammenti con link renderizzabili
 */
const linkifyText = (text, keyPrefix = '') => {
  if (!text) return [text]
  const parts = text.split(URL_REGEX)
  if (parts.length === 1) return [text]
  return parts.map((part, i) => {
    if (URL_REGEX.test(part)) {
      URL_REGEX.lastIndex = 0
      const cleanUrl = part.replace(/[.),:;!?]+$/, '')
      if (!isSafeUrl(cleanUrl)) return cleanUrl
      return (
        <a
          key={`${keyPrefix}url-${i}`}
          href={cleanUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="gf-chat__link"
        >
          {cleanUrl}
        </a>
      )
    }
    return part
  })
}

/**
 * Converte **testo** in nodi React con enfasi e linkifica gli URL
 * @param {string} text - Testo con marcatori bold e possibili URL
 * @returns {import('react').ReactNode[]} Frammenti renderizzabili
 */
const renderBoldText = (text = '') => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.flatMap((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{linkifyText(part.slice(2, -2), `b${i}-`)}</strong>
    }
    return linkifyText(part, `t${i}-`)
  })
}

const GigiChatMessage = ({ message, isReadOnly }) => {
  const { message: messageText = '', user, createdAt, attachments = [] } = message
  const isUser = user !== 'Nora'
  const time = formatTime(createdAt)
  const [showImg, setShowImg] = useState(false)
  const [media, setMedia] = useState(null)

  const renderParagraphs = () => {
    if (messageText?.includes('<html')) {
      return (
        <div className="gf-chat__html-preview">
          <p>{t.clickToPreviewMessage}</p>
          <button
            type="button"
            className="gf-btn gf-btn--ghost gf-btn--sm"
            onClick={() => {
              const previewWindow = window.open('', '_blank')
              previewWindow.document.write(messageText)
              previewWindow.document.close()
            }}
          >
            <Icon name="eye" size={12} />
            {t.previewMessage}
          </button>
        </div>
      )
    }

    return sanitizeMsg(messageText)?.map((p, index) => {
      if (p.includes('<img')) {
        const mediaUrl = p.match(/<img\s+[^>]*\bsrc="(https?:\/\/[^">]+)"/i)?.[1]
        if (!mediaUrl) {
          const textOnly = p
            .replace(/<img\s+[^>]*>/gi, '')
            .replace(/<img[^>]*$/i, '')
            .trim()
          return (
            <p key={index} className="gf-chat__para">
              {renderBoldText(textOnly || p)}
            </p>
          )
        }
        return (
          <img
            key={index}
            src={mediaUrl}
            alt=""
            className="gf-chat__inline-img"
            onClick={() => {
              setMedia({ url: mediaUrl, type: 'image' })
              setShowImg(true)
            }}
          />
        )
      }
      return (
        <p key={index} className="gf-chat__para">
          {renderBoldText(p)}
        </p>
      )
    })
  }

  const standaloneImages =
    attachments?.filter(({ type, inBodyEmail }) => type?.includes('image') && !inBodyEmail) ?? []
  const bodyEmailImages =
    attachments?.filter(({ type, inBodyEmail }) => type?.includes('image') && inBodyEmail) ?? []
  const audioAttachments = attachments?.filter(({ type }) => type?.includes('audio')) ?? []
  const fileAttachments =
    attachments?.filter(({ type }) => {
      const ty = type || ''
      return ty && !ty.includes('image') && !ty.includes('audio')
    }) ?? []

  const hasAttachmentSection =
    standaloneImages.length +
      audioAttachments.length +
      fileAttachments.length +
      bodyEmailImages.length >
    0

  const openAttachmentPreview = (att) => {
    setMedia(att)
    setShowImg(true)
  }

  const hasMainList = standaloneImages.length + fileAttachments.length + audioAttachments.length > 0

  const userMeta = isReadOnly ? `${time} · ${t.gfLetto}` : time
  const aiMeta = `${time} · Nora`

  return (
    <div className={`gf-chat__msg${isUser ? ' gf-chat__msg--user' : ' gf-chat__msg--ai'}`}>
      <div className="gf-chat__bubble">
        <div className="gf-chat__bubble-body">{renderParagraphs()}</div>

        {hasAttachmentSection && (
          <div className="gf-chat__attachments-block">
            <hr className="gf-chat__attachments-rule" />
            <div className="gf-chat__attachments-title">{t.gfAllegati}</div>
            {(standaloneImages.length > 0 ||
              fileAttachments.length > 0 ||
              audioAttachments.length > 0) && (
              <ul className="gf-chat__attachments-list">
                {standaloneImages.map((att, index) => (
                  <li key={`img-${index}`}>
                    <button
                      type="button"
                      className="gf-chat__att-row"
                      onClick={() => openAttachmentPreview(att)}
                    >
                      <Icon name="photo" size={13} />
                      <span>
                        {t.gfImmagine} {index + 1}
                      </span>
                    </button>
                  </li>
                ))}
                {fileAttachments.map((att, index) => (
                  <li key={`file-${index}`}>
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gf-chat__att-row gf-chat__att-row--link"
                    >
                      <Icon name="paperclip" size={13} />
                      <span>
                        {t.gfAllegatoFile} {index + 1}
                      </span>
                    </a>
                  </li>
                ))}
                {audioAttachments.map((att, index) => (
                  <li key={`aud-${index}`} className="gf-chat__att-audio">
                    <span className="gf-chat__att-audio-meta">
                      <Icon name="mic" size={12} />
                      {t.gfAudio} {index + 1}
                    </span>
                    <audio controls src={att.url} className="gf-chat__att-native-audio" />
                  </li>
                ))}
              </ul>
            )}
            {bodyEmailImages.length > 0 && (
              <div
                className={
                  'gf-chat__attachments-inline' +
                  (hasMainList ? '' : ' gf-chat__attachments-inline--solo')
                }
              >
                <p className="gf-chat__attachments-inline-hint">{t.gfAllegatiNelCorpoEmail}</p>
                <ul className="gf-chat__attachments-list">
                  {bodyEmailImages.map((att, index) => (
                    <li key={`body-img-${index}`}>
                      <button
                        type="button"
                        className="gf-chat__att-row gf-chat__att-row--muted"
                        onClick={() => openAttachmentPreview(att)}
                      >
                        <Icon name="photo" size={13} />
                        <span>
                          {t.gfImmagine} {index + 1} · {t.gfInCorpoEmail}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {isUser ? (
          <footer className="gf-chat__bubble-foot gf-chat__bubble-foot--in">{userMeta}</footer>
        ) : (
          <footer className="gf-chat__bubble-foot gf-chat__bubble-foot--in gf-chat__bubble-foot--ai-in">
            {aiMeta}
          </footer>
        )}
      </div>

      {!isUser && (
        <footer className="gf-chat__bubble-foot gf-chat__bubble-foot--out">{aiMeta}</footer>
      )}

      <Modal
        className="gf-dialog-modal gf-dialog-modal--media"
        style={{ width: 'min(90vw, 900px)' }}
        contentStyle={{ padding: 0 }}
        visible={showImg}
        onHide={() => setShowImg(false)}
      >
        <div className="gf-dialog-modal__media-wrap">
          <button
            type="button"
            className="gf-dialog-modal__media-close"
            aria-label={t.gfChiudiAnteprima}
            onClick={() => setShowImg(false)}
          >
            <Icon name="x" size={14} />
          </button>
          {media?.type?.includes('image') && <img src={media?.url} alt="" />}
          {media?.type?.includes('audio') && (
            <div className="gf-dialog-modal__media-audio">
              <audio controls src={media?.url} />
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

/**
 * Elenco messaggi della chat laterale Gigi con bolle stilizzate
 * @param {Object} props
 * @param {Array} props.messages - Messaggi della conversazione
 * @param {boolean} [props.isReadOnly] - Se true mostra lo stato "letto" sui messaggi utente
 * @returns {JSX.Element}
 */
export const GigiChatMessages = ({ messages = [], isReadOnly = true }) => {
  if (!messages?.length) {
    return (
      <div className="gf-chat__thread gf-chat__thread--empty" role="log">
        <i
          className="pi pi-comments"
          style={{ fontSize: 28, opacity: 0.35, color: 'var(--ink-400)' }}
        />
        <span style={{ fontSize: 13, color: 'var(--ink-400)' }}>{t.nessunMessaggio}</span>
      </div>
    )
  }
  return (
    <div className="gf-chat__thread" role="log" aria-live="polite">
      {messages.map((message, index) => (
        <GigiChatMessage key={index} message={message} isReadOnly={isReadOnly} />
      ))}
    </div>
  )
}
